/**
 * Khớp `scene.location` với location trong Bank — bản FE của `resolveLocationByName` (admin-service,
 * `src/utils/filmLocationName.ts`). 260807.
 *
 * 🔴 Vì sao cần: hai nguồn tên KHÔNG bao giờ bằng nhau. Bank đặt tên từ **heading** của scene
 * (`INT. MORTAIN MANSION - NIGHT` → "MORTAIN MANSION"), còn `scene.location` do **AI ghi tự do**
 * ("Mortain Mansion - Party Room", "Foggy Night Road"). UI trước đây so `norm(a) === norm(b)` nên
 * không tìm thấy plate, ô ảnh location của scene hiện trống dù Bank đã có ảnh — user báo 07/08.
 *
 * Ba bước khớp, giống hệt backend để hai bên không nói hai đằng:
 *   1. chuẩn hoá bằng nhau
 *   2. chứa nhau (spot ⊂ cha, hoặc ngược lại) — nhiều ứng viên thì lấy tên DÀI nhất
 *   3. trùng ≥1 token đặc trưng (≥4 ký tự)
 * Không khớp gì ⇒ null (KHÔNG đoán bừa: thà ô trống còn hơn gắn nhầm ảnh phòng khác).
 *
 * Để trong `utils/` (không import antd) vì antd v6 + react-router v7 không load được dưới jest của
 * react-scripts — logic muốn test được thì phải nằm ngoài file có antd. Xem CLAUDE.md.
 */

/** Bỏ ngoặc phụ chú và mọi ký tự ngoài chữ cái, viết hoa — để "Mortain Mansion - Party Room" so được với "MORTAIN MANSION". */
export const normalizeLocationName = (loc?: string | null): string =>
    String(loc || '').toUpperCase().replace(/\(.*?\)/g, '').replace(/[^A-Z]/g, '');

const wordsOf = (s?: string | null): string[] =>
    String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

export function resolveLocationByName<T extends { name?: string | null }>(
    sceneLocation: string | null | undefined,
    bank: T[] | null | undefined,
): T | null {
    const want = normalizeLocationName(sceneLocation);
    if (!want || !bank?.length) return null;

    const exact = bank.find((l) => normalizeLocationName(l.name) === want);
    if (exact) return exact;

    /**
     * Chấm điểm mọi ứng viên rồi lấy cao nhất — giữ y luật của backend (`utils/filmLocationName.ts`),
     * sửa một bên thì sửa cả hai, nếu không UI lại nói khác thứ engine thật sự dùng.
     *
     * DANH TỪ CUỐI của tên scene ("garden", "hallway", "road") là nơi chốn cụ thể được gọi tên nên nặng
     * nhất. Cách cũ (chứa-nhau → lấy tên dài nhất) sai hai ca thật: "Mortain Mansion - Back Garden" ra
     * MORTAIN MANSION (cảnh vườn lĩnh ảnh sảnh), và "Mortain Mansion - Grand Hallway" không chứa
     * "MANSION HALLWAY" (chữ GRAND chen giữa) nên cũng rơi về tên khu.
     */
    const sceneWords = wordsOf(sceneLocation);
    const headNoun = sceneWords[sceneWords.length - 1] || '';
    const sceneToks = new Set(sceneWords.filter((w) => w.length >= 4));

    let best: T | null = null;
    let bestScore = 0;
    for (const l of bank) {
        const n = normalizeLocationName(l.name);
        if (!n) continue;
        let score = 0;
        if (headNoun && wordsOf(l.name).includes(headNoun)) score += 10;
        if (want.endsWith(n) || n.endsWith(want)) score += 5;
        if (want.includes(n) || n.includes(want)) score += 2;
        wordsOf(l.name).forEach((w) => { if (w.length >= 4 && sceneToks.has(w)) score += 1; });
        if (score > bestScore) { bestScore = score; best = l; }
    }
    return bestScore >= 1 ? best : null;
}
